import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// Grommet

// Appt
import Box from '../components/simple/box/Box.js'
import Login from '../components/compound/login/Login.js'
import PasswordResetRequest from '../components/compound/login/PasswordResetRequest.js'
import Text from '../components/simple/text/Text.js'
import UserVerification from '../components/compound/login/UserVerification.js'
import Image from '../components/simple/image/Image.js'
import Checkbox from '../components/simple/input/CheckBox.js'
import Anchor from '../components/simple/anchor/Anchor.js'


// Services
import ApiService from '../services/ApiService'
import SSOService from '../services/SSOService.js'
import NotificationService from '../services/NotificationService'
import PasswordService from '../services/PasswordService'
import LocalStorageService from '../services/LocalStorageService.js'

// Stores
import { useStore } from 'react-context-hook'

// Error handler
// import useErrorHandler from '../utils/useErrorHandler'

// Theme
// import { hostTheme } from 'utilities/hostTheme'
import ActionButton from '../components/compound/actionButton/ActionButton.js'

const GoogleTagManager = React.lazy(() => import('../components/compound/banner/GoogleTagManager.js'))

import { CONNECTION_DEBUG, SECURITY_ENFORCE_PASSWORD_POLICY } from "../env";
import TextInput from '../components/simple/input/TextInput.js'
import AsyncCreatableSelect from 'react-select/async-creatable'

function OnboardingScreen(props) {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [createPassword, setCreatePassword] = useState(false)
  const [viewLogin, setLoginView] = useState(true)
  const [verificationNeeded, setVerification] = useState(false)
  const [userValues, setUserValues] = useState()

  const [profile, setProfile] = useState(null)

  const [error, setError] = useState(null)

  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [sessionToken, setSessionToken] = useState(null)

  const [fromSSO, setFromSSO] = useState(false)
  const [userName, setUserName] = useState(() => new URLSearchParams(window.location.search).get('name') || '')
  const [userEmail, setUserEmail] = useState(() => new URLSearchParams(window.location.search).get('email') || '')
  const [verificationCode, setVerificationCode] = useState('')
  const [userOrganisation, setUserOrganisation] = useState({
    urn: '',
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    countryCode: '',
    country: ''
  })
  const [selectedSchool, setSelectedSchool] = useState(null)
  const [schoolSearchError, setSchoolSearchError] = useState(null)

  useEffect(() => {
    // check if logged in using local storage, if so redirect to dashboard
    const apiToken = LocalStorageService.get('apiToken')
    if (apiToken) {
      void navigate('/dashboard')
    }
  }, [])

  // school search needs an SSO session token, which only exists after the first step
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const schoolName = params.get('school')
    const urn = params.get('urn')
    if (schoolName && !selectedSchool) createNewSchools(schoolName, urn?.trim() || '')

    if (!urn || !sessionToken) return
    SSOService.searchSchools(urn.trim())
      .then(schools => {
        const match = schools.find(s => String(s.urn) === urn.trim())
        if (match) setOrganisationFromSchool({ school: match })
      })
      .catch(err => console.error('Unable to prefill school:', err))
  }, [sessionToken])

  const getOnboardingStatus = async (ssoSessionToken) => {

    if (!ssoSessionToken) {
      ssoSessionToken = await SSOService.getOrCreateSSOSessionToken()
      setSessionToken(ssoSessionToken)
    }
    // still no SSO session token, there's a problem
    if (!ssoSessionToken) {
      NotificationService.showError('Session error, please refresh the page to continue.')
      return
    }

    if (process.env.FEATURE_SSO_ENABLED !== "true") {
      return
    }

    if (!fromSSO) {
      // don't do anything if we're not from the sso
      return
    }

    SSOService.getProfile(ssoSessionToken).then(response => {

      const profile = response.centralProfile

      if (response.status === 'loggedin') {
        props.handleLogin(response.loginData)
          // if you're here, you're logged in
        void navigate('/dashboard')
        return
      }

      if (profile) {
        setUserName((profile?.firstName || '') + ' ' + (profile?.lastName || ''))

        setProfile(profile)

        setUserOrganisation({
          urn: profile?.recommendedOrg?.urn || '',
          name: profile?.recommendedOrg?.name || '',
          addressLine1: profile?.recommendedOrg?.addressLine1 || '',
          addressLine2: profile?.recommendedOrg?.addressLine2 || '',
          city: profile?.recommendedOrg?.city || '',
          postcode: profile?.recommendedOrg?.postcode || '',
          countryCode: profile?.recommendedOrg?.countryCode || '',
          country: profile?.recommendedOrg?.country || ''
        })

        if (profile?.recommendedOrg?.name) {
          setSelectedSchool({
            value: profile.recommendedOrg.urn || profile.recommendedOrg.name,
            label: profile.recommendedOrg.name,
            school: profile.recommendedOrg
          })
        }
      }
    })

  }

  // useEffect(() => {
  //   getOnboardingStatus()
  // }, [])

  const loadSchoolOptions = async (searchTerm) => {
    if (searchTerm.trim().length < 3) {
      setSchoolSearchError(null)
      return []
    }

    try {
      setSchoolSearchError(null)
      const schools = await SSOService.searchSchools(searchTerm.trim())

      return schools.map(school => ({
        value: school.urn,
        label: `${school.establishmentName} (${school.postcode || school.urn})`,
        school
      }))
    } catch (searchError) {
      console.error('Unable to search for schools:', searchError)
      setSchoolSearchError(searchError.message)
      return []
    }
  }

  const setOrganisationFromSchool = (school) => {
    if (!school) {
      setSelectedSchool(null)
      setUserOrganisation({
        urn: '',
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        postcode: '',
        countryCode: '',
        country: ''
      })
      return
    }

    setSelectedSchool({
      value: school.school.name,
      label: `${school.school.establishmentName}`,
      school: school.school
    })
    setUserOrganisation({
      urn: school.school.urn || '',
      name: school.school.establishmentName || '',
      addressLine1: school.school.addressLineOne || '',
      addressLine2: school.school.addressLineTwo || '',
      city: school.school.town || '',
      postcode: school.school.postcode || '',
      countryCode: 'GB',
      country: 'United Kingdom'
    })
  }

  const createNewSchools = (name, urn = '') => {
    const school = {
      urn,
      establishmentName: name,
      addressLineOne: '',
      addressLineTwo: '',
      town: '',
      postcode: ''
    }

    setSelectedSchool({
      value: name,
      label: name,
      school: school
    })
    setUserOrganisation({
      urn: school.urn || '',
      name: school.establishmentName || '',
      addressLine1: school.addressLineOne || '',
      addressLine2: school.addressLineTwo || '',
      city: school.town || '',
      postcode: school.postcode || '',
      countryCode: 'GB',
      country: 'United Kingdom'
    })
  }

  const steps = [
    {
      question: "What's your work email address?",
      key: 'email',
      validation: () => {
        if (!userEmail) return 'Please enter a valid email address.'
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(userEmail)) return 'Please enter a valid email address.'
        if (!acceptedTerms) return 'Please agree to the terms and conditions and privacy policy to continue.'
        return null
      },
      content: (
        <Box height='100%' gap='small'>
          <TextInput
            label='Email'
            size='xlarge'
            required
            type='email'
            placeholder='name@yourschool.sch.uk'
            value={userEmail}
            onChange={e => setUserEmail(e.target.value)}
          />
          <Text color='white' size='small'>We'll use this to create your account and send you walkthrough video.</Text>
          <Checkbox
            label={<Text color='white'>I agree to the <Anchor color='white' href='https://www.behavioursmart.co.uk/terms-and-conditions-20261005' label='terms and conditions' target='_blank' /> and <Anchor color='white' href='https://www.behavioursmart.co.uk/legal/privacy-notice/' label='privacy policy' target='_blank' /></Text>}
            checked={acceptedTerms}
            onChange={e => setAcceptedTerms(e.target.checked)}
          />
        </Box>
      )
    },
    {
      question: "What's your full name?",
      subQuestion: "We'll use this on your account and in our emails to you.",
      key: 'name',
      validation: () => {
        if (!userName) return 'Please enter a name to continue.'
        if (!userName.includes(' ')) return 'Please enter your full name.'

        return null
      },
      content: (
        <Box height='100%'>
          <TextInput
            label='Name'
            size='xlarge'
            placeholder='First name and surname'
            required
            value={userName}
            onChange={e => setUserName(e.target.value)}
          />
        </Box>
      )
    },
    {
      question: "What's the name of your school or service?",
      subQuestion: "We'll set up your trial account for this school or service. Trialling across a trust? Choose one school to start with.",
      key: 'organisation',
      validation: () => {
        if (!selectedSchool) return 'Please select your school or service.'
        return null
      },
      content: (
        <Box height='100%'>
          <AsyncCreatableSelect
            inputId='organisation-school'
            aria-label='Organisation'
            isClearable
            cacheOptions
            defaultOptions={false}
            loadOptions={loadSchoolOptions}
            value={selectedSchool}
            onChange={setOrganisationFromSchool}
            onCreateOption={createNewSchools}
            placeholder='Search for your school'
            noOptionsMessage={({ inputValue }) => {
              if (schoolSearchError) return schoolSearchError
              if (inputValue.trim().length < 3) return 'Start typing to search'
              return 'No matches found. Check the spelling, or add your school or service manually.'
            }}
          />
        </Box>
      )
    },
    {
      question: "Check your email for a code.",
      subQuestion: "It can take a minute to arrive. If you can't see it, check your junk folder.",
      key: 'verification',
      validation: (value) => {
        if (!verificationCode) return 'Please enter the verification code to continue.'
        return null
      },
      content: (
        <Box height='100%' gap='small'>
          <Text color='white'>We've sent a verification code to {userEmail}. Enter it below to continue.</Text>
          <TextInput
            label='Verification Code'
            size='xlarge'
            width='450px'
            required
            value={verificationCode}
            onChange={e => setVerificationCode(e.target.value)}
          />
        </Box>
      )
    },
    {
      question: "You're all set",
      key: 'final',
      content: (
        <Box height='100%' gap='xsmall'>
          <Text color='white'>A short guided tour will show you round and help you make your first record.</Text>
          <Text color='white'>✅&nbsp;&nbsp;Record incidents quickly with a short, guided report</Text>
          <Text color='white'>✅&nbsp;&nbsp;See patterns, triggers and emerging risks early</Text>
          <Text color='white'>✅&nbsp;&nbsp;Build support plans and reports for governors and inspection</Text>

          <Text color='white' margin={{ top: 'medium' }}>No cost and no obligation. If you don't continue after 30 days, your data is securely and permanently deleted.</Text>
        </Box>
      )
    }
  ]

  const [currentStep, setCurrentStep] = useState(0)
  const totalSteps = steps.length

  const nextStep = async () => {

    const validationError = steps[currentStep].validation?.()
    if (validationError) {
      NotificationService.error(validationError)
      return
    }

    if (userEmail && !sessionToken) {
      try {
        let ssoSessionToken = await SSOService.getOrCreateSSOSessionToken(userEmail)
        setSessionToken(ssoSessionToken)
      } catch (err) {
        NotificationService.error(err?.message || 'Failed to get SSO session token');
        return
      }
    }

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1)
    }

    if (currentStep === totalSteps - 1) {
      submitProfileAndLogin()
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  useEffect(() => {
    try {
      const eventKey = steps?.[currentStep]?.key
      if (!eventKey) return
      ApiService.syntheticEvent(`frontend/get-started/${eventKey}`)
    } catch (err) {
      console.error('Failed to send synthetic event', err)
    }
  }, [currentStep])

  const currentContent = steps[currentStep].content

  const handleNextClick = () => {
    nextStep()
  }

  const handlePrevClick = () => {
    prevStep()
  }

  const submitProfileAndLogin = async () => {

    let ssoSessionToken = SSOService.getSSOSessionToken()

    let nameParts = userName.trim().split(' ')

    let firstName = nameParts[0] || ''
    let lastName = nameParts.slice(1).join(' ') || ''


    // update the profile

    if (fromSSO) {
      // await SSOService.updateProfile(ssoSessionToken, firstName, lastName, selectedSchool?.value)
      throw new Error('SSO not enabled');
    } else {
      let response = await SSOService.completeSignup({
        sessionToken: ssoSessionToken,
        firstName,
        lastName,
        schoolURN: userOrganisation?.urn,
        schoolCustomName: userOrganisation?.name,
        verificationCode
      })


      if (!response?.loginData) {
        NotificationService.error(response.message || 'Failed to complete signup');
        if (response.goto && steps.find(s => s.key === response.goto)) {
          setCurrentStep(steps.findIndex(s => s.key === response.goto))
        }
        return
      }

      props.handleLogin(response.loginData)
      // if you're here, you're logged in
      void navigate('/dashboard')


    }
  }


  return (
    <>
      <React.Suspense fallback={null}>
        <GoogleTagManager enabled />
      </React.Suspense>
      <Box height='100%' justify='flex-start' alignSelf='flex-start' width='large'>
        <Box width='small' alignSelf='flex-start' height='xsmall' margin={{ bottom: 'medium' }}>
          <Image fit='contain' src='/besmart-logo-white.png?r=001' />
        </Box>

        <Box border={[{ color: 'brand', side: 'bottom' }]} pad={{ bottom: 'medium' }}>
          <Text color='white' weight='bold' size='xlarge'>Start your 30-day free trial</Text>
          <Text color='white' size='medium'>{currentStep < totalSteps - 1 ? "Full access for your whole school or setting, at no cost. Five quick questions and you're in." : "Email verified. One more click and you're in."}</Text>
        </Box>

        <Box margin={{ vertical: 'medium' }} gap='none' direction='column'>
          <Text color='white'>Step {currentStep + 1} of {totalSteps}</Text>
          <Text color='white' size='xxlarge' weight='bold' margin={{ top: 'xxsmall', bottom:'small' }}>
            {steps[currentStep].question}
          </Text>
           <Box gap='medium' direction='column'>
            {currentContent}
          </Box>
          {steps[currentStep].subQuestion && <Text color='white' size='small'>{steps[currentStep].subQuestion}</Text>}
        </Box>


<Box margin={{ vertical: 'medium' }} flex direction='row' gap='small'>
      {currentStep > 0 && (
          <ActionButton
            label='Back'
            secondary
            onClick={handlePrevClick}
          />
        )}
        <ActionButton
          label={currentStep < totalSteps - 1 ? 'Continue' : 'Start recording now'}
          primary
          onClick={handleNextClick}
        />

      </Box>

        <Box >
          {viewLogin && !verificationNeeded &&
            <>
              <Box pad={{ vertical: 'medium' }} width='medium'>
                {error?.length > 0 && <Box width='medium' height='xsmall' align='center' margin={{ bottom: 'small' }} pad='small' background='primary' round='small'>
                  <Text color='secondary' size='medium' weight='bold'>{error}</Text>
              </Box>}
              </Box>



            </>
          }

        </Box>
      </Box>

    </>
  )
}

export default OnboardingScreen
